import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SessionUser } from '@blogforge/shared';

interface AuthState {
  user: SessionUser | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  /** Workspace the UI is currently acting on — sent as `X-Workspace-Id`. */
  activeWorkspaceId: string | null;
  login: (user: SessionUser, refreshToken: string) => void;
  logout: () => void;
  setUser: (user: SessionUser) => void;
  setActiveWorkspace: (workspaceId: string) => void;
}

/**
 * The signed-in session, persisted to localStorage.
 *
 * `user` is the `SessionUser` projection the API returns, so
 * `isPlatformAdmin` is available here to gate the admin area. That flag is a
 * display fact only — every admin route re-checks it against the database
 * (see apps/server/src/admin/middleware.ts), so tampering with this store
 * buys 403s, not access.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      refreshToken: null,
      isAuthenticated: false,
      activeWorkspaceId: null,

      login: (user, refreshToken) => set({ user, refreshToken, isAuthenticated: true }),

      logout: () =>
        set({ user: null, refreshToken: null, isAuthenticated: false, activeWorkspaceId: null }),

      setUser: (user) => set({ user }),

      setActiveWorkspace: (activeWorkspaceId) => set({ activeWorkspaceId }),
    }),
    {
      name: 'blogforge-auth',
      // Only persist the refresh token, user and active workspace — not the
      // access token (that's in an httpOnly cookie).
      partialize: (state) => ({
        user: state.user,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        activeWorkspaceId: state.activeWorkspaceId,
      }),
    },
  ),
);
